import * as yup from 'yup';
import { CoursePathway } from '../constants/courseEnums';
import { COURSE_DURATION_REQUIRED_MESSAGE, hasCourseDuration } from './courseDuration';

export const COURSE_TEXT_LIMITS = {
  title: 200,
  description: 1000,
  certificateDescriptionMin: 150,
  certificateDescriptionMax: 600,
  seoTitle: 100,
  seoDescription: 200,
  paymentLink: 500,
  pointTitle: 200,
} as const;

export const COURSE_PRICE_MAX = 999999;

export const COURSE_MAX_BENEFITS = 6;

export const COURSE_MAX_RECOMMENDED = 3;

export const COURSE_RECOMMENDED_LIMIT_MESSAGE =
  'The maximum number of recommended courses has been reached. Please remove a course to add another one.';

export const COURSE_RECOMMENDED_SELECTED_MESSAGE = `You have already selected ${COURSE_MAX_RECOMMENDED} recommended courses.`;

const COURSE_IMAGE_MAX_SIZE = 10 * 1024 * 1024;
const COURSE_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

export const COURSE_IMAGE_ACCEPT = 'image/jpeg,image/png';

const PAYMENT_LINK_PATH = /^(?:\/hs)?\/payments\/[^/]+\/?$/;

const PAYMENT_LINK_ERROR =
  'Payment link must be an https HubSpot payment link, for example https://payments-eu1.hubspot.com/payments/XXXX';

const isHubspotPaymentLink = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && PAYMENT_LINK_PATH.test(url.pathname);
  } catch {
    return false;
  }
};

const trimmedLength = (value: unknown) => (typeof value === 'string' ? value.trim().length : 0);

const optionalText = (max: number, label: string) =>
  yup
    .string()
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .max(max, `${label} must not exceed ${max} characters`)
    .optional();

const durationSchema = (label: string) =>
  yup
    .number()
    .transform((value) => (Number.isNaN(value) ? 0 : value))
    .min(0, `${label} cannot be negative`)
    .test('min-one', `${label} must be at least 1 month`, (value) => !value || value >= 1);

const imageFileSchema = (isRequired: boolean) =>
  yup
    .mixed<File>()
    .nullable()
    .test('required', 'Course image is required', (value) => !isRequired || value instanceof File)
    .test(
      'file-type',
      'Course image must be a JPG or PNG file',
      (value) => !(value instanceof File) || COURSE_IMAGE_TYPES.includes(value.type),
    )
    .test(
      'file-size',
      'Course image must not exceed 10MB',
      (value) => !(value instanceof File) || value.size <= COURSE_IMAGE_MAX_SIZE,
    );

interface CourseFormSchemaOptions {
  isCreate: boolean;
}

export const createCourseFormSchema = ({ isCreate }: CourseFormSchemaOptions) =>
  yup.object({
    title: yup
      .string()
      .trim()
      .required('Course title is required')
      .max(
        COURSE_TEXT_LIMITS.title,
        `Course title must not exceed ${COURSE_TEXT_LIMITS.title} characters`,
      ),
    type: yup.string().required('Type is required'),
    level: yup.string().required('Level is required'),
    category: yup.string().required('Category is required'),
    leadTeacher: yup.string().required('Course Lead is required'),
    description: yup
      .string()
      .trim()
      .required('Course description is required')
      .max(
        COURSE_TEXT_LIMITS.description,
        `Course description must not exceed ${COURSE_TEXT_LIMITS.description} characters`,
      ),
    pathway: isCreate
      ? yup
          .string()
          .required('Pathway is required')
          .oneOf(Object.values(CoursePathway), 'Select a valid pathway')
      : yup.string().optional(),
    price: yup
      .number()
      .typeError('Price must be a number')
      .required('Price is required')
      .min(0, 'Price cannot be negative')
      .max(COURSE_PRICE_MAX, `Price must not exceed ${COURSE_PRICE_MAX}`),
    discountPrice: yup
      .number()
      .nullable()
      .optional()
      .min(0, 'Discount price cannot be negative')
      .max(COURSE_PRICE_MAX, `Discount price must not exceed ${COURSE_PRICE_MAX}`)
      .test(
        'less-than-price',
        'Discount price must be less than base price',
        function lessThanPrice(value) {
          if (value === undefined || value === null) return true;
          return value < Number(this.parent.price ?? 0);
        },
      ),
    durationFullTime: durationSchema('Full time duration').test(
      'course-duration',
      COURSE_DURATION_REQUIRED_MESSAGE,
      function atLeastOneDuration(value) {
        return hasCourseDuration(value, this.parent.durationPartTime);
      },
    ),
    durationPartTime: durationSchema('Part time duration'),
    certificateDescription: yup
      .string()
      .transform((value) => (typeof value === 'string' ? value.trim() : value))
      .optional()
      .test(
        'certificate-description-length',
        `Certificate description must be between ${COURSE_TEXT_LIMITS.certificateDescriptionMin} and ${COURSE_TEXT_LIMITS.certificateDescriptionMax} characters`,
        (value) => {
          if (!value) return true;
          return (
            value.length >= COURSE_TEXT_LIMITS.certificateDescriptionMin &&
            value.length <= COURSE_TEXT_LIMITS.certificateDescriptionMax
          );
        },
      ),
    paymentLink: optionalText(COURSE_TEXT_LIMITS.paymentLink, 'Payment link').test(
      'hubspot-payment-link',
      PAYMENT_LINK_ERROR,
      (value) => !value || isHubspotPaymentLink(value),
    ),
    seoTitle: optionalText(COURSE_TEXT_LIMITS.seoTitle, 'SEO title'),
    seoDescription: optionalText(COURSE_TEXT_LIMITS.seoDescription, 'SEO description'),
    imageFile: imageFileSchema(isCreate),
  });

export interface CoursePointValue {
  title: string;
  description: string;
}

export const getCoursePointErrors = (point: CoursePointValue, includeRequired = true) => {
  const titleLength = trimmedLength(point.title);
  const descriptionLength = trimmedLength(point.description);
  if (!titleLength && !descriptionLength) return {};

  let title: string | undefined;
  if (titleLength > COURSE_TEXT_LIMITS.pointTitle) {
    title = `Point title must not exceed ${COURSE_TEXT_LIMITS.pointTitle} characters`;
  } else if (!titleLength && includeRequired) {
    title = 'Point title is required';
  }

  return {
    title,
    description:
      !descriptionLength && includeRequired ? 'Point description is required' : undefined,
  };
};

export const hasCoursePointErrors = (points: CoursePointValue[]) =>
  points.some((point) => {
    const errors = getCoursePointErrors(point);
    return !!errors.title || !!errors.description;
  });

export type CourseFormTab = 'cover' | 'course-page' | 'settings';

const COURSE_FORM_FIELD_TABS: Record<string, CourseFormTab> = {
  imageFile: 'cover',
  title: 'cover',
  type: 'cover',
  level: 'cover',
  category: 'cover',
  leadTeacher: 'cover',
  pathway: 'cover',
  description: 'cover',
  durationFullTime: 'settings',
  durationPartTime: 'settings',
  price: 'settings',
  discountPrice: 'settings',
  paymentLink: 'settings',
  certificateDescription: 'settings',
  seoTitle: 'settings',
  seoDescription: 'settings',
};

export const COURSE_FORM_TAB_FIELDS: Record<CourseFormTab, string[]> = {
  cover: Object.keys(COURSE_FORM_FIELD_TABS).filter((f) => COURSE_FORM_FIELD_TABS[f] === 'cover'),
  'course-page': [],
  settings: Object.keys(COURSE_FORM_FIELD_TABS).filter(
    (f) => COURSE_FORM_FIELD_TABS[f] === 'settings',
  ),
};

export const getCourseFormErrorTab = (fields: string[]): CourseFormTab | null => {
  const tabs: CourseFormTab[] = ['cover', 'course-page', 'settings'];
  return tabs.find((tab) => fields.some((field) => COURSE_FORM_FIELD_TABS[field] === tab)) ?? null;
};

const SERVER_ERROR_FIELDS: Array<[RegExp, string]> = [
  [/course with title .* already exists|course title/i, 'title'],
  [/payment link/i, 'paymentLink'],
  [/certificate description/i, 'certificateDescription'],
  [/seo title/i, 'seoTitle'],
  [/seo description/i, 'seoDescription'],
  [/discount price/i, 'discountPrice'],
  [/course description/i, 'description'],
  [/duration/i, 'durationFullTime'],
  [/price/i, 'price'],
  [/file must be an image|file size must not exceed 10mb|course image/i, 'imageFile'],
];

export const getCourseServerErrorField = (message?: string): string | null => {
  if (!message) return null;
  return SERVER_ERROR_FIELDS.find(([pattern]) => pattern.test(message))?.[1] ?? null;
};
